import { formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store"
import { useGetDashboardCount } from "@/services";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux"

const useFinancialOverview = () => {
  const currentFinanceYear = useSelector((state: RootState) => state.currentData.currentFinanceYear);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);

  const {
    data: dashboardCounts,
    isLoading: isDashboardCountLoading,
    isRefetching: isDashboardCountRefetching,
    refetch: refetchDashboardCounts,
  } = useGetDashboardCount({
    communityId: communityData?.communityId,
    year: currentFinanceYear,
    queryParams: {
      enabled: !!(communityData?.communityId && currentFinanceYear),
    },
  });

  useEffect(() => {
    if (currentFinanceYear) {
      refetchDashboardCounts();
    }
  }, [currentFinanceYear]);

  const dashboardData = dashboardCounts?.data || dashboardCounts || {};

  const overviewRows = useMemo(() => {
    const revenueBudget = Number(dashboardData?.budgetIncome || 0);
    const revenueActual = Number(dashboardData?.actualRecievables || 0);
    const expenseBudget = Number(dashboardData?.budgetExpense || 0);
    const expenseActual = Number(dashboardData?.actualExpense || 0);
    const surplusBudget = revenueBudget - expenseBudget;
    const surplusActual = revenueActual - expenseActual;

    const toRow = (label: string, budget: number, actual: number) => ({
      label,
      budget: formatAmount(budget),
      actual: formatAmount(actual),
      variance: formatAmount(budget - actual),
    });

    return [
      toRow("Revenue", revenueBudget, revenueActual),
      toRow("Expense", expenseBudget, expenseActual),
      toRow("Surplus/Deficit(-)", surplusBudget, surplusActual),
    ];
  }, [dashboardData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "label",
        header: "",
        accessorFn: (row: any) => row.label,
      },
      {
        accessorKey: "budget",
        header: "Budget",
        accessorFn: (row: any) => row.budget,
        meta: { align: "right" },
      },
      {
        accessorKey: "actual",
        header: "Actual",
        accessorFn: (row: any) => row.actual,
        meta: { align: "right" },
      },
      {
        accessorKey: "variance",
        header: "Variance",
        accessorFn: (row: any) => row.variance,
        meta: { align: "right" },
      },
    ],
    []
  );
  
  return {
    currentFinanceYear,
    dashboardData,
    overviewRows,
    columns,
    isLoading: isDashboardCountLoading || isDashboardCountRefetching
  }
}

export default useFinancialOverview;
